import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import type { RouterOutputs } from "@/lib/trpc";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Users, CheckCircle2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { TrustNote } from "./TrustNote";

/**
 * Shared Receipts feed — renders the server's already-safe, deterministic
 * projection VERBATIM. No render-time LLM, no paraphrase, no client dedup, no
 * tone dots, no per-person counts, no rankings. Own row shows
 * "Approved · Visible to team" (no lock icon inside the feed).
 *
 * Server guarantees (Phase 1, IT-tested): one row per (user, week), weekly
 * preferred, cascaded dailies excluded — the client just maps `rows`.
 */
type Feed = RouterOutputs["team"]["sharedReceipts"];
type Row = Feed["rows"][number];

/** Compact relative time, e.g. "just now", "2h ago", "3d ago", else a date. */
function relTime(iso: string | null, isAr?: boolean): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return isAr ? "الآن" : "just now";
  if (mins < 60) return isAr ? `قبل ${mins} د` : `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return isAr ? `قبل ${hrs} س` : `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return isAr ? `قبل ${days} ي` : `${days}d ago`;
  return new Date(iso).toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", { month: "short", day: "numeric" });
}

function dayLabel(periodKey: string, isAr?: boolean): string {
  return new Date(periodKey + "T00:00:00.000Z").toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function initials(name: string): string {
  return (name || "—")
    .split(" ")
    .slice(0, 2)
    .map(p => p[0])
    .join("")
    .toUpperCase();
}

function ReceiptRow({ row }: { row: Row }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [expanded, setExpanded] = useState(false);
  const headline = row.headline || row.lines[0] || "";
  // Highlights to show on expand: drop the first line ONLY if it's the line we
  // already used as the headline (avoid duplicating it).
  const detailLines = row.lines.filter((l, i) => !(i === 0 && l === headline));
  // The week's full approved set (weekly first, then dailies). Older payload
  // shapes may lack `items`; degrade to the representative-only view.
  const items = (row as any).items ?? [];
  const multi = items.length > 1;
  const rowShipped: string[] = (row as any).shipped ?? [];
  const hasDetails =
    multi || detailLines.length > 0 || rowShipped.length > 0 || row.blockers.length > 0;

  // Collapsed cue — what's available to open. Everything shown on expand is an
  // already-approved, viewer-scoped projection rendered verbatim (no
  // regeneration, no extra fields). We surface only a highlight/receipt count
  // (never a blocker count) so the collapsed feed never reads as a per-person
  // metric.
  const cue = multi
    ? isAr
      ? `عرض التفاصيل المُشارَكة · ${items.length} ملخّصات هذا الأسبوع`
      : `View shared details · ${items.length} receipts this week`
    : detailLines.length
      ? isAr
        ? `عرض التفاصيل المُشارَكة · ${detailLines.length} من أبرز الإنجازات`
        : `View shared details · ${detailLines.length} highlight${detailLines.length === 1 ? "" : "s"}`
      : isAr
        ? "عرض التفاصيل المُشارَكة"
        : "View shared details";

  return (
    <div className="flex items-start gap-3 py-3" data-testid="shared-receipt-row">
      <Avatar className="h-9 w-9 border shrink-0">
        <AvatarFallback className="text-xs font-medium bg-sand text-ink">
          {initials(row.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">
            {row.name}
            {row.isOwn ? (isAr ? " (أنت)" : " (You)") : ""}
          </span>
          <span className="ms-auto text-xs text-muted-foreground shrink-0">{row.asOf}</span>
        </div>
        {expanded && multi ? null : (
          // Show the FULL headline — it's the receipt's shared summary and must
          // be readable at a glance. (Previously line-clamp-2 cut longer
          // headlines mid-sentence with "…"; the "View shared details" expander
          // reveals the HIGHLIGHTS, not the rest of the headline, so clamping
          // the headline just hid shared content.)
          <p className="text-sm text-foreground/90 mt-0.5">{headline}</p>
        )}
        {row.approvedAt ? (
          <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="shared-receipt-approved-at">
            {isAr ? "معتمد" : "Approved"} · {relTime(row.approvedAt, isAr)}
          </div>
        ) : null}

        {hasDetails ? (
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-coral hover:underline"
            data-testid="shared-receipt-toggle"
            aria-expanded={expanded}
          >
            {expanded ? (isAr ? "إخفاء التفاصيل" : "Hide details") : cue}
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} />
          </button>
        ) : null}

        {expanded && hasDetails ? (
          multi ? (
            // The WHOLE week's approved receipts — each one its own approved,
            // viewer-scoped projection (an approved Monday stays reachable
            // after Tuesday's approval).
            <div className="mt-2 space-y-2" data-testid="shared-receipt-details">
              {items.map((it: any) => (
                <div
                  key={`${it.kind}-${it.periodKey}`}
                  className="rounded-lg border border-border bg-card/60 p-3"
                  data-testid="shared-receipt-item"
                >
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {it.kind === "weekly" ? (isAr ? "أسبوعي" : "Weekly") : dayLabel(it.periodKey, isAr)}
                    {it.approvedAt ? (
                      <span className="normal-case tracking-normal text-muted-foreground/80">
                        {" "}· {isAr ? "اعتُمد" : "Approved"} {relTime(it.approvedAt, isAr)}
                      </span>
                    ) : null}
                  </div>
                  {/* Uniform card shape: DAY → headline → details. The row's
                      summary line hides while expanded, so the headline still
                      appears exactly once at any moment. */}
                  {it.headline ? (
                    <div className="mt-0.5 text-sm font-medium text-foreground/90">{it.headline}</div>
                  ) : null}
                  {it.shipped?.length ? (
                    <div className="mt-1.5">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "المنجزات" : "Shipped"}</div>
                      <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                        {it.shipped.map((l: string, i: number) => (
                          <li key={i}>{l}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {it.lines?.length ? (
                    <div className={it.shipped?.length ? "mt-2" : "mt-1"}>
                      {it.shipped?.length ? (
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "أبرز الإنجازات" : "Highlights"}</div>
                      ) : null}
                      <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                        {it.lines.map((l: string, i: number) => (
                          <li key={i}>{l}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {it.blockers?.length ? (
                    <div className="mt-2">
                      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "المعوقات" : "Blockers"}</div>
                      <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                        {it.blockers.map((b: string, i: number) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-2 rounded-lg border border-border bg-card/60 p-3" data-testid="shared-receipt-details">
              {(row as any).shipped?.length ? (
                <div className="mb-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "المنجزات" : "Shipped"}</div>
                  <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                    {(row as any).shipped.map((l: string, i: number) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {detailLines.length ? (
                <div>
                  {(row as any).shipped?.length ? (
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "أبرز الإنجازات" : "Highlights"}</div>
                  ) : null}
                  <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                    {detailLines.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {row.blockers.length ? (
                <div className="mt-2">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "المعوقات" : "Blockers"}</div>
                  <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                    {row.blockers.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )
        ) : null}

        {row.isOwn && row.visibility ? (
          <div className="flex items-center gap-1 text-xs text-emerald-700 mt-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>{row.visibility}</span>
          </div>
        ) : null}
      </div>
      {/* "shared" affordance — a people glyph, NOT a tone dot, NOT a lock. */}
      <Users className="h-4 w-4 text-muted-foreground/70 shrink-0 mt-1" aria-hidden />
    </div>
  );
}

export function SharedReceiptsFeed({ rows }: { rows: Row[] }) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  // Presentation-only ordering: pin the viewer's OWN row first (post-approval
  // "is mine up?" check is the most common glance), teammates stay in the
  // server's newest-first order below. Stable sort preserves that order.
  const ordered = [...rows].sort((a, b) => Number(b.isOwn) - Number(a.isOwn));
  return (
    <section className="rounded-2xl border border-border bg-card/60 p-4" data-testid="shared-receipts">
      <div className="text-xs font-semibold tracking-wide text-muted-foreground mb-1">
        {isAr ? "الملخّصات المُشارَكة" : "SHARED RECEIPTS"}
      </div>
      <div className="divide-y divide-border">
        {ordered.map(r => (
          <ReceiptRow key={`${r.userId}-${r.asOf}`} row={r} />
        ))}
      </div>
      <TrustNote>
        {isAr
          ? "تُشارَك الملخّصات من قِبل أصحابها بعد الاعتماد. ترى ما يختارون مشاركته."
          : "Receipts are shared by people after approval. You see what they choose to share."}
      </TrustNote>
    </section>
  );
}
